import { useState } from "react";
import { Phone, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Images from "../../../utils/Images";
import { Link, useNavigate } from "react-router-dom";
import authService from "../../../api/AuthService";
import { toast } from "react-toastify";

export default function ForgotPassword() {
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleContinue = async () => {
    if (phone.trim()) {
      setIsSubmitting(true);
      const response = await authService.forgotPassword(phone);
      const data = await response?.data?.data;
      console.log("data",data)
      if (data && data.status_code === 200) {
        toast.success(data.reason || "Reset code sent successfully");
        navigate("/forgot-password/reset", {
          state: {
            method: "phone",
            contact: phone,
            consumer_slug: data.data.slug,
          },
        });
      } else {
        toast.error("Failed to send reset code");
        setIsSubmitting(false)
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <img
        src="https://images.unsplash.com/photo-1551632811-561732d1e306?q=80&w=1600&auto=format&fit=crop"
        alt="bg"
        className="object-cover absolute inset-0 w-full h-full"
      />
      <div className="absolute inset-0 bg-black/60" />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex relative z-10 justify-center items-start pt-10"
      >
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </motion.div>

      <div className="flex relative z-10 justify-center items-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="p-6 mt-4 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <h2 className="text-lg font-semibold text-center">Reset Password</h2>
          <p className="mt-1 text-xs text-center text-gray-500">
            Enter your phone number to receive a reset code
          </p>

          <div className="mt-4 space-y-4">
            {/* Phone Number Input */}
            <PhoneNumberField
              value={phone}
              onChange={setPhone}
              delay={0.1}
            />

            {/* Continue Button */}
            <motion.button
              onClick={handleContinue}
              disabled={!phone.trim() || isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                {isSubmitting ? "Sending..." : "Send Reset Code"}
                <motion.div
                  animate={{ x: [0, 3, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <ArrowRight size={16} />
                </motion.div>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>

            {/* Back to Sign In */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              <Link
                to="/signin"
                className="block text-xs text-center text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Back to Sign In
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Phone Number Field Component for Ghana (+233) - Same as in SignUp
function PhoneNumberField({ value, onChange, delay = 0, error = null }) {
  const [displayValue, setDisplayValue] = useState(value || "");

  const formatPhoneNumber = (input) => {
    const digits = input.replace(/\D/g, "");

    if (digits.startsWith("0")) {
      return digits.slice(0, 10);
    }
    return digits.slice(0, 9);
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input);
    setDisplayValue(formatted);

    // Convert to international format for payload
    let internationalNumber = "";
    if (formatted.startsWith("0")) {
      // If starts with 0, remove it and add 233
      internationalNumber = "233" + formatted.slice(1);
    } else if (formatted.length === 9) {
      // If 9 digits without leading 0, add 233
      internationalNumber = "233" + formatted;
    }

    onChange(internationalNumber);
  };

  const getDisplayText = () => {
    if (!displayValue) return "";
    if (displayValue.startsWith("0")) {
      return displayValue;
    }
    return displayValue;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-transparent to-emerald-400/30 transition-all duration-300 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
        <div className="flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 ring-gray-200/50 shadow-sm backdrop-blur-md transition-all duration-300 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white">
          <motion.div
            initial={{ rotate: 0, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Phone
              size={18}
              className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700"
            />
          </motion.div>
          <div className="flex flex-1 items-center">
            <div className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-l-lg border-r border-gray-200">
              233
            </div>
            <input
              type="tel"
              value={getDisplayText()}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              className="flex-1 px-3 py-1 text-sm font-medium bg-transparent rounded-r-lg transition-all duration-300 outline-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900 focus:outline-none focus:ring-0 invalid:shadow-none invalid:outline-none invalid:ring-0"
              maxLength={displayValue.startsWith("0") ? 10 : 9}
            />
          </div>
        </div>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {displayValue.startsWith("0")
          ? "Enter 10 digits (including the leading 0)"
          : "Enter 9 digits (without leading 0)"}
        <br />
        <span className="text-gray-400">Format: 233XXXXXXXXX</span>
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}
